import { getVariable } from '@/services/backstage/BackstageProvider';
import { useAuthenticationService } from '@/services/authentication/authentication-service';
import { useLocationService } from '@/services/location/LocationService';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { useEffect, useRef } from 'react';
import { OptimiserJob, OptimiserStatus } from './optimiser.types';

interface BatchJobStateChangedEvent {
  id: string;
  batchId: string;
  started?: string;
  finished?: string;
  properties: string;
}

const BATCH_JOB_STATE_CHANGED = 'BatchJobStateChanged';
const MULTI_DAY_TEMPLATED_TYPE = 'OptimiseMultiDayTemplatedStatus';
const INITIAL_RETRY_DELAYS_MS = [1000, 2000, 5000, 10000];

const toHubUrl = async (tenantId: string, clientId: string, delegatedSessionId?: string) => {
  const apiUrl = await getVariable('apiUrl');
  if (!apiUrl) {
    throw new Error('apiUrl is not defined.');
  }

  const url = new URL(apiUrl, window.location.origin);
  url.pathname = url.pathname.replace(/\/graphql(?:\.generated)?$/, '/hubs/rostering-realtime');
  url.searchParams.set('tenant-id', tenantId);
  url.searchParams.set('client-id', clientId);
  if (delegatedSessionId) {
    url.searchParams.set('x-delegation-session-id', delegatedSessionId);
  }
  return url.toString();
};

const toOptimiserJob = (change: BatchJobStateChangedEvent): OptimiserJob | undefined => {
  let raw: any;

  try {
    raw = JSON.parse(change.properties);
  } catch (error) {
    console.warn('Failed to parse optimiser SignalR properties.', { properties: change.properties, error });
    return undefined;
  }

  const type = raw.type ?? raw.Type;
  if (type !== MULTI_DAY_TEMPLATED_TYPE) {
    return undefined;
  }

  const locationId = raw.locationId ?? raw.LocationId;
  const from = raw.from ?? raw.From;
  const to = raw.to ?? raw.To;
  const rawStatus = raw.status ?? raw.Status;

  if (!locationId || !from || !to || rawStatus === undefined || rawStatus === null) {
    console.warn('Invalid optimiser SignalR properties.', {
      raw,
      locationId,
      from,
      to,
      rawStatus
    });
    return undefined;
  }

  const status = String(rawStatus) as OptimiserStatus;

  const complete =
    !!change.finished ||
    status === OptimiserStatus.FINISHED ||
    status === OptimiserStatus.ERROR;

  const effectiveStatus =
    complete && status !== OptimiserStatus.ERROR
      ? OptimiserStatus.FINISHED
      : status;

  return {
    batchId: change.batchId,
    locationIds: [locationId],
    startDate: new Date(from),
    endDate: new Date(to),
    started: change.started ? new Date(change.started) : undefined,
    complete,
    status: effectiveStatus
  };
};

const wait = async (delayMs: number) => {
  await new Promise(resolve => window.setTimeout(resolve, delayMs));
};

export const useOptimiserBatchSignalR = (upsertJob: (job: OptimiserJob) => void) => {
  const [accessToken, tenantId, delegatedSessionId] = useAuthenticationService(state => [
    state.user?.access_token,
    state.getEffectiveTenantId(),
    state.delegatedSession?.delegationSessionId
  ]);
  const selectedLocationId = useLocationService(state => state.selectedLocation?.id);

  const upsertJobRef = useRef(upsertJob);

  useEffect(() => {
    upsertJobRef.current = upsertJob;
  }, [upsertJob]);

  useEffect(() => {
    if (!accessToken || !tenantId || !selectedLocationId) {
      return;
    }

    let disposed = false;
    let connection: HubConnection | undefined;
    let retryInFlight = false;

    const ensureStarted = async () => {
      if (retryInFlight) {
        return;
      }

      retryInFlight = true;

      const clientId = (await getVariable('authIdentityClientId')) || '';
      const hubUrl = await toHubUrl(tenantId, clientId, delegatedSessionId);

      if (!connection) {
        if (disposed) {
          retryInFlight = false;
          return;
        }

        connection = new HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => accessToken,
            withCredentials: false
          })
          .withAutomaticReconnect()
          .configureLogging(LogLevel.Warning)
          .build();

        connection.on(BATCH_JOB_STATE_CHANGED, (event: BatchJobStateChangedEvent) => {
          console.info('Optimiser SignalR event received.', event);

          const job = toOptimiserJob(event);
          if (!job) {
            console.warn('Optimiser SignalR event ignored.', event);
            return;
          }

          console.info('Optimiser SignalR job upsert.', job);
          upsertJobRef.current(job);
        });

        connection.onclose(error => {
          if (disposed) {
            return;
          }

          console.warn('Optimiser SignalR connection closed. Retrying start.', error);
          void ensureStarted().catch(startError => {
            console.error('Failed to restart optimiser batch SignalR connection.', startError);
          });
        });
      }

      let attempt = 0;

      try {
        while (!disposed) {
          if (
            connection.state === HubConnectionState.Connected ||
            connection.state === HubConnectionState.Connecting ||
            connection.state === HubConnectionState.Reconnecting
          ) {
            return;
          }

          try {
            await connection.start();
            await connection.invoke('JoinLocationGroup', selectedLocationId);

            console.info('Optimiser SignalR connection started.', {
              connectionId: connection.connectionId,
              state: connection.state,
              locationId: selectedLocationId,
              tenantId,
              delegatedSessionId
            });
            return;
          } catch (error) {
            const retryDelayMs = INITIAL_RETRY_DELAYS_MS[Math.min(attempt, INITIAL_RETRY_DELAYS_MS.length - 1)];
            attempt += 1;

            console.error('Failed to start optimiser batch SignalR connection.', {
              attempt,
              retryDelayMs,
              error
            });

            if (disposed) {
              return;
            }

            await wait(retryDelayMs);
          }
        }
      } finally {
        retryInFlight = false;
      }
    };

    void ensureStarted().catch(error => {
      console.error('Failed to initialise optimiser batch SignalR connection.', error);
    });

    return () => {
      disposed = true;

      if (connection && connection.state !== HubConnectionState.Disconnected) {
        void connection.stop();
      }
    };
  }, [accessToken, tenantId, delegatedSessionId, selectedLocationId]);
};
